import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { HeroCard } from '@/components/ui/HeroCard';
import { SectionCard } from '@/components/ui/SectionCard';
import { motion } from 'framer-motion';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HexColorPicker } from 'react-colorful';

interface ProgressBarConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

// Converter rgba string para hex

// Mapear espessura para labels amigáveis

// Converter rgba string para hex
const rgbaToHex = (rgba: string): string => {
  if (rgba.startsWith('#')) return rgba;
  
  const match = rgba.match(/\d+/g);
  if (match) {
    const [r, g, b] = match.map(Number);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
  return '#FF0000';
};

export const ProgressBarConfig: React.FC<ProgressBarConfigProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  const [showProgressBar, setShowProgressBar] = useState(
    features.progressBar?.enabled || false
  );
  const [progressBarHeight, setProgressBarHeight] = useState(
    features.progressBar?.height ||
    features.progressBarHeight ||
    12
  );
  const [progressBarColor, setProgressBarColor] = useState(
    rgbaToHex(features.progressBar?.color || features.progressBarColor || '#FF0000')
  );
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const handleHeightChange = (value: number[]) => {
    const height = value[0];
    setProgressBarHeight(height);
    
    setFeature('progressBar', {
      ...(features.progressBar || {}),
      height,
      color: progressBarColor
    });
    setFeature('progressBarHeight', height);
  };

  const handleColorChange = (color: string) => {
    setProgressBarColor(color);
    
    setFeature('progressBar', {
      ...(features.progressBar || {}),
      height: progressBarHeight,
      color
    });
    setFeature('progressBarColor', color);
  };

  const handleShowProgressBarChange = (value: boolean) => {
    setShowProgressBar(value);
    setFeature('progressBar', {
      ...(features.progressBar || {}),
      enabled: value,
      height: progressBarHeight,
      color: progressBarColor
    });

    if (value && features.showControls) {
      // Apenas o toggle mestre dos controles (showControls) é incompatível
      // com a barra fake. Não mexemos em showProgressBar (sub-recurso dos
      // controles) — assim, ao reativar os controles, a preferência do
      // usuário sobre a barra interna é preservada.
      setFeature('showControls', false);
      toast.info(translateInterfaceText("Controles desativados automaticamente. Barra fake e controles não podem estar ativos ao mesmo tempo."));
    }
  };

  useEffect(() => {
    setShowProgressBar(features.progressBar?.enabled || false);
    setProgressBarHeight(
      features.progressBar?.height ||
      features.progressBarHeight ||
      12
    );
    setProgressBarColor(
      rgbaToHex(features.progressBar?.color || features.progressBarColor || '#FF0000')
    );
  }, [features.progressBar]);

  return (
    <div className="space-y-4 py-4 px-6">
      {/* HeroCard - Controle Mestre */}
      <HeroCard
        icon={<BarChart3 className="w-full h-full" strokeWidth={2} />}
        title={translateInterfaceText("Barra Fake")}
        description={translateInterfaceText("Mostre visualmente o avanço do vídeo e aumente a retenção.")}
        isActive={showProgressBar}
        onToggle={handleShowProgressBarChange}
      />

      {/* Conteúdo - Só aparece quando ativado */}
      {showProgressBar && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-4"
        >
          {/* BLOCO 1 - Estilo da Barra */}
          <SectionCard
            title={translateInterfaceText("Estilo da Barra")}
            description=""
          >
            <div className="space-y-5">
              {/* Cor da barra */}
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-[#374151]">
                  {translateInterfaceText("Cor da barra")}</Label>
                <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: progressBarColor }}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <HexColorPicker 
                      color={progressBarColor} 
                      onChange={handleColorChange}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Espessura da barra */}
              <div className="space-y-3">
              <Label className="text-[12px] font-medium text-[#374151]">
                {translateInterfaceText("Espessura da barra")}</Label>
                <div className="space-y-2">
                  <Slider 
                    min={2} 
                    max={12} 
                    step={1}
                    defaultValue={[progressBarHeight]} 
                    value={[progressBarHeight]}
                    onValueChange={handleHeightChange} 
                    className="w-full"
                  />
                </div>
                <p className="text-[11px] text-[#6B7280]">
                  {translateInterfaceText("Altura visual da barra durante a reprodução")}</p>
              </div>
            </div>
          </SectionCard>

        </motion.div>
      )}

      {/* Estado OFF - Mensagem discreta */}
      {!showProgressBar && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="text-center py-6"
        >
          <p className="text-[12px] text-[#9CA3AF]">
            {translateInterfaceText("Ative para exibir a barra de progresso no vídeo.")}</p>
        </motion.div>
      )}
    </div>
  );
};